// Fresh v2 service for live search — wraps the same underlying GraphQL
// subscription the legacy app uses (GraphqlAPI.subscribeOnSearch(), which
// resolves to subscribe-on-search-v2.graphql over the shared WS transport),
// but decoupled from the old $fetch/service-worker-proxy plumbing and with
// its own result normalization, independent of the legacy UI's shape.
import { GraphqlAPI } from "../../core/graphql-api";
import { permissionsForRole } from "../composables/usePermissions.js";
import { highlightMatch } from "../composables/useHighlight.js";

const graphqlServer = import.meta.env.VITE_GRAPHQL_SERVER_V1;

function formatProject(project, query) {
  return {
    ...project,
    userRole: project.sessionUserRole,
    permissions: permissionsForRole(project.sessionUserRole),
    totalDocuments: project.totalDocumentCount,
    styledDescription: highlightMatch(project.description, query),
  };
}

// Card grids elsewhere (Docs.vue, Project.vue) pass a `project` prop with
// { id, userRole, permissions } to DocumentCardMenu for permission gating —
// search results carry the same project data alongside each document, so we
// shape it the same way here rather than inventing a different contract.
function formatDocument(document, project) {
  const preview = document.preview;
  return {
    ...document,
    mimeType: document.originMimeType,
    preview: preview ? `data:image/png;base64,${preview}` : "",
    projectId: project?.id ?? null,
    projectName: project?.name ?? null,
    project: project
      ? { id: project.id, name: project.name, userRole: project.sessionUserRole, permissions: permissionsForRole(project.sessionUserRole) }
      : null,
  };
}

let textResultSeq = 0;
function formatTextResult({ project, document, textEntry, page }, query) {
  return {
    // DocumentTextSearchResult has no id field of its own in the schema.
    id: `text-${document.id}-${page}-${textResultSeq++}`,
    documentId: document.id,
    documentName: document.name,
    projectId: project?.id ?? null,
    projectName: project?.name ?? null,
    page,
    text: textEntry,
    styledSnippet: highlightMatch(textEntry, query),
  };
}

function formatAnnotationResult({ project, document, documentObject }, query) {
  // CommentAnnotation has no `text` field — show a neutral label instead of
  // highlighting nothing.
  const text = documentObject.text ?? null;
  return {
    id: documentObject.id,
    documentId: document.id,
    documentName: document.name,
    projectId: project?.id ?? null,
    projectName: project?.name ?? null,
    page: documentObject.page,
    type: documentObject.__typename,
    text,
    styledSnippet: text ? highlightMatch(text, query) : "Comment",
  };
}

function normalize(item, query) {
  switch (item.__typename) {
    case "ProjectSearchResult":
      return { type: "project", data: formatProject(item.project, query) };
    case "DocumentSearchResult":
      return { type: "document", data: formatDocument(item.document, item.project) };
    case "DocumentTextSearchResult":
      return { type: "text", data: formatTextResult(item, query) };
    case "DocumentObjectSearchResult":
      return { type: "annotation", data: formatAnnotationResult(item, query) };
    default:
      return null;
  }
}

export default class SearchService {
  constructor() {
    this._graphqlAPIService = null;
  }

  // Built lazily on first use, not in the constructor — see the comment on
  // the same getter in projectService.js: module-level singleton instances of
  // this class can be constructed before login finishes, which would
  // otherwise bake in a missing/stale token permanently.
  get graphqlAPIService() {
    if (!this._graphqlAPIService) {
      this.accessToken = localStorage.getItem("accessToken");
      this._graphqlAPIService = new GraphqlAPI(graphqlServer, this.accessToken);
    }
    return this._graphqlAPIService;
  }

  // Subscribes to live search results for `query`. Each incoming item is
  // normalized to { type: 'project'|'document'|'text'|'annotation', data }
  // and handed to onResult. Returns an unsubscribe function.
  subscribe(query, { onResult, onError, onComplete }) {
    const observable = this.graphqlAPIService.subscribeOnSearch(query);
    const subscription = observable.subscribe(
      ({ data }) => {
        const item = data?.search;
        if (!item) return;
        const result = normalize(item, query);
        if (result) onResult(result);
      },
      (error) => {
        subscription.unsubscribe();
        onError?.(error);
      },
      () => {
        subscription.unsubscribe();
        onComplete?.();
      }
    );
    return () => subscription.unsubscribe();
  }
}
